import { toast } from 'sonner';

// Constants
import { STATUS_CODE, MESSAGES, STATUS_MESSAGES } from '@shared/constants';
import {
  AUTH_ERROR_MESSAGES,
  SILENT_ERROR_MESSAGES,
  FIREBASE_ERROR_CODES,
} from '@/constants';

// Types
import { ApiResponse } from '../types';

/**
 * Handle errors in server actions - re-throws 401 and 403 role mismatch errors, returns formatted response for others
 * @param error - The error to handle
 * @returns Formatted error response or re-throws 401/403 role mismatch errors
 *
 * @example
 * ```typescript
 * try {
 *   const response = await httpClient(...);
 *   return response;
 * } catch (error) {
 *   return handleActionError<MyDataType>(error);
 * }
 * ```
 */
export const handleActionError = <T = null>(error: unknown): ApiResponse<T> => {
  // Re-throw 401 and 403 role mismatch errors to be handled by HOCs
  if (
    error instanceof Error &&
    (error.message === STATUS_MESSAGES[STATUS_CODE.UNAUTHORIZED] ||
      error.message === AUTH_ERROR_MESSAGES.ROLE_MISMATCH ||
      error.message === STATUS_MESSAGES[STATUS_CODE.FORBIDDEN])
  ) {
    return {
      data: null as T,
      error: error.message,
      status: STATUS_CODE.FORBIDDEN,
    };
  }

  // Return formatted error response for other errors
  return {
    data: null as T,
    error: error instanceof Error ? error.message : MESSAGES.GENERAL_ERROR,
    status: STATUS_CODE.INTERNAL_SERVER_ERROR,
  };
};

/**
 * Normalize error message
 * @param error - The error to normalize
 * @returns The normalized error message
 */
const normalizeErrorMessage = (error: unknown): string => {
  const errorMessage =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : (STATUS_MESSAGES[STATUS_CODE.INTERNAL_SERVER_ERROR] ??
          MESSAGES.UNKNOWN_ERROR);

  // Handle Firebase auth/user-disabled error
  if (errorMessage === FIREBASE_ERROR_CODES.USER_DISABLED) {
    return AUTH_ERROR_MESSAGES.ACCOUNT_DEACTIVATED;
  }

  return errorMessage;
};

/**
 * Show error toast notification
 * If silent errors, do not show toast
 */
export const showErrorToast = (
  error: unknown,
  silentErrors: string[] = [],
): string | number | null => {
  const errorMessage = normalizeErrorMessage(error);

  if ([...SILENT_ERROR_MESSAGES, ...silentErrors].includes(errorMessage)) {
    return null;
  }

  return toast.error(errorMessage);
};
